/**
 * RemotePlayerRenderer — paint other players inside a hosted PaintedScene.
 *
 * SMA-42 Phase 2. The PaintedOverworldShell instantiates one of these per
 * Colyseus room and re-points it at whichever painted scene is currently
 * active. The renderer keeps a sprite per remote player whose
 * `sceneZoneId` matches the active zone, places it at the player's
 * normalized `(localX, localY)` projected against the painted scene's
 * canvas, and runs a small interpolation toward the latest server
 * position so frame-rate independent jitter doesn't show through.
 *
 * Sprite specs:
 *   - Reuses the 128×128 Waker walk/idle spritesheets that PaintedScene
 *     already preloads via `preloadWakerSpritesheets()`. There's no
 *     per-character art yet (Phase 3+); painting all remote players with
 *     the Waker sheet keeps Phase 2 unblocked while the cosmetic layer
 *     catches up.
 *   - Origin matches the local Waker (0.5, 1.0) so the sprite sits on the
 *     ground and the perspective scale curve treats local vs. remote the
 *     same.
 *
 * The renderer is Phaser-aware but takes a minimal sprite-factory
 * interface so it can be unit-tested without a live Phaser scene.
 */

import * as Phaser from 'phaser';
import type { PaintedScene } from './PaintedScene';
import {
  samplePerspectiveScale,
  type PerspectiveSample,
} from './sceneAssetSpec.schema';

/** Snapshot of one remote player's position in zone-local coordinates. */
export interface RemotePlayerSnapshot {
  sessionId: string;
  name: string;
  zoneId: string;
  localX: number;
  localY: number;
  /** Attendance state from the server schema. When 'ghost', the sprite
   *  renders at 30% alpha with a sine-wave pulse. */
  attendanceState?: string;
}

/** Interpolation rate per second. 1.0 = snap; lower = smoother. */
const INTERP_RATE_PER_SEC = 8;

/** Ghost rendering: base alpha and sine-wave pulse parameters. */
const GHOST_ALPHA_BASE = 0.3;
const GHOST_ALPHA_AMPLITUDE = 0.1;
const GHOST_ALPHA_SPEED = 0.003;

interface RenderedRemote {
  sprite: Phaser.GameObjects.Sprite;
  shadow: Phaser.GameObjects.Ellipse;
  label: Phaser.GameObjects.Text | null;
  /** Most recent server-confirmed position. */
  targetX: number;
  targetY: number;
  /** Smoothed render position. */
  renderX: number;
  renderY: number;
  prevRenderX: number;
  prevRenderY: number;
  /** True when the player is in ghost attendance state. */
  isGhost: boolean;
}

const DEFAULT_LABEL_FONT = "'Almendra', serif";
const DEFAULT_LABEL_COLOR = '#eae0c8';
const REMOTE_DEPTH = 99;
const REMOTE_SHADOW_DEPTH = 98;

/** Optional injection seam — keeps the renderer testable without Phaser. */
export interface RemotePlayerRendererOptions {
  showNameplates?: boolean;
}

export class RemotePlayerRenderer {
  private active: PaintedScene | null = null;
  private readonly remotes = new Map<string, RenderedRemote>();
  /** Sticky snapshots so we can rebuild on scene swap without a fresh sync. */
  private readonly latestSnapshots = new Map<string, RemotePlayerSnapshot>();
  private readonly showNameplates: boolean;

  constructor(opts: RemotePlayerRendererOptions = {}) {
    this.showNameplates = opts.showNameplates ?? true;
  }

  /**
   * Switch which painted scene the renderer paints into. Called by the
   * shell whenever the local player crosses a zone boundary. Tears down
   * existing sprites in the old scene and rebuilds for the new zone using
   * the most recent snapshots.
   */
  attachTo(scene: PaintedScene | null): void {
    this.tearDownSprites();
    this.active = scene;
    if (!scene) return;
    // Defer rebuild until the painted scene is `ready` so the walker
    // animations and asset spec are wired up.
    if (scene.ready) {
      this.rebuildSpritesForActiveZone();
    } else {
      scene.events.once('painted-scene:ready', () => this.rebuildSpritesForActiveZone());
    }
  }

  /**
   * Drop a remote player. Called when a Colyseus session leaves or when
   * its zoneId changes away from the active zone.
   */
  removeRemote(sessionId: string): void {
    this.latestSnapshots.delete(sessionId);
    const rec = this.remotes.get(sessionId);
    if (!rec) return;
    rec.sprite.destroy();
    rec.shadow.destroy();
    if (rec.label) rec.label.destroy();
    this.remotes.delete(sessionId);
  }

  /**
   * Apply a remote player snapshot. If the snapshot's zone doesn't match
   * the active zone, the sprite is removed (or never created). If it
   * matches, the sprite is created or its target position updated.
   */
  upsertRemote(snapshot: RemotePlayerSnapshot): void {
    this.latestSnapshots.set(snapshot.sessionId, snapshot);

    if (!this.active) return;
    const activeZone = this.active.manifestKey;
    if (snapshot.zoneId !== activeZone) {
      // Player drifted to another zone — no longer rendered here.
      const existing = this.remotes.get(snapshot.sessionId);
      if (existing) {
        existing.sprite.destroy();
        existing.shadow.destroy();
        if (existing.label) existing.label.destroy();
        this.remotes.delete(snapshot.sessionId);
      }
      return;
    }

    const w = this.active.scale.width;
    const h = this.active.scale.height;
    const px = snapshot.localX * w;
    const py = snapshot.localY * h;

    const isGhost = snapshot.attendanceState === 'ghost';

    const existing = this.remotes.get(snapshot.sessionId);
    if (existing) {
      existing.targetX = px;
      existing.targetY = py;
      existing.isGhost = isGhost;
      return;
    }

    const sprite = this.buildSprite(snapshot.name);
    if (!sprite) return;
    sprite.setPosition(px, py);

    const shadow = this.active.add.ellipse(px, py, 28, 10, 0x000000, 0.3);
    shadow.setDepth(REMOTE_SHADOW_DEPTH);

    let label: Phaser.GameObjects.Text | null = null;
    if (this.showNameplates && snapshot.name) {
      label = this.active.add.text(px, py - 96, snapshot.name, {
        fontFamily: DEFAULT_LABEL_FONT,
        fontSize: '12px',
        color: DEFAULT_LABEL_COLOR,
        shadow: { offsetX: 1, offsetY: 1, color: '#000000', blur: 3, fill: true },
      })
        .setOrigin(0.5, 1)
        .setDepth(REMOTE_DEPTH + 1);
    }

    // Apply initial ghost alpha if needed.
    if (isGhost) {
      sprite.setAlpha(GHOST_ALPHA_BASE);
      shadow.setAlpha(GHOST_ALPHA_BASE);
      if (label) label.setAlpha(GHOST_ALPHA_BASE);
    }

    this.remotes.set(snapshot.sessionId, {
      sprite,
      shadow,
      label,
      targetX: px,
      targetY: py,
      renderX: px,
      renderY: py,
      prevRenderX: px,
      prevRenderY: py,
      isGhost,
    });
  }

  /**
   * Per-frame interpolation update. Call from PaintedOverworldShell.update().
   * Smooths the gap between snapshots and applies the perspective-scale
   * curve so remote players shrink with depth like the local walker does.
   */
  update(deltaMs: number): void {
    if (!this.active) return;
    const dt = deltaMs / 1000;
    const t = Math.min(1, INTERP_RATE_PER_SEC * dt);
    const w = this.active.scale.width;
    const h = this.active.scale.height;
    const now = this.active.game.getTime();
    const profile: ReadonlyArray<PerspectiveSample> | null = this.active.assetSpec?.perspective ?? null;

    for (const rec of this.remotes.values()) {
      rec.prevRenderX = rec.renderX;
      rec.prevRenderY = rec.renderY;
      rec.renderX += (rec.targetX - rec.renderX) * t;
      rec.renderY += (rec.targetY - rec.renderY) * t;
      rec.sprite.setPosition(rec.renderX, rec.renderY);
      rec.shadow.setPosition(rec.renderX, rec.renderY);
      if (rec.label) rec.label.setPosition(rec.renderX, rec.renderY - 96);

      if (profile) {
        const ny = rec.renderY / h;
        const scale = samplePerspectiveScale(ny, profile);
        rec.sprite.setScale(scale);
        rec.shadow.setScale(scale * 0.9, scale * 0.45);
      }

      // Ghost rendering: sine-wave alpha pulse, forced idle animation.
      if (rec.isGhost) {
        const ghostAlpha = GHOST_ALPHA_BASE + GHOST_ALPHA_AMPLITUDE * Math.sin(now * GHOST_ALPHA_SPEED);
        rec.sprite.setAlpha(ghostAlpha);
        rec.shadow.setAlpha(ghostAlpha);
        if (rec.label) rec.label.setAlpha(ghostAlpha);
        // Ghosts always idle — skip walk animation.
        this.applyDirectionalAnim(rec, 0, 0, false);
      } else {
        // Restore full alpha for non-ghost players (in case state changed).
        if (rec.sprite.alpha < 1) {
          rec.sprite.setAlpha(1);
          rec.shadow.setAlpha(1);
          if (rec.label) rec.label.setAlpha(1);
        }
        // Cheap walk/idle pick: if the position barely moved this frame, idle.
        const dx = rec.renderX - rec.prevRenderX;
        const dy = rec.renderY - rec.prevRenderY;
        const moved = Math.hypot(dx, dy) > 0.4;
        this.applyDirectionalAnim(rec, dx, dy, moved);
      }
      // Suppress unused-warning when w isn't read otherwise.
      void w;
    }
  }

  /** Stop tracking everything. Idempotent. */
  destroy(): void {
    this.tearDownSprites();
    this.latestSnapshots.clear();
    this.active = null;
  }

  // ── internal helpers ────────────────────────────────────────────────────

  private tearDownSprites(): void {
    for (const rec of this.remotes.values()) {
      rec.sprite.destroy();
      rec.shadow.destroy();
      if (rec.label) rec.label.destroy();
    }
    this.remotes.clear();
  }

  private rebuildSpritesForActiveZone(): void {
    if (!this.active) return;
    const activeZone = this.active.manifestKey;
    for (const snapshot of this.latestSnapshots.values()) {
      if (snapshot.zoneId === activeZone) {
        this.upsertRemote(snapshot);
      }
    }
  }

  private buildSprite(_name: string): Phaser.GameObjects.Sprite | null {
    if (!this.active) return null;
    if (!this.active.textures.exists('waker-walk-up')) return null;
    const sprite = this.active.add.sprite(0, 0, 'waker-walk-up', 0);
    sprite.setOrigin(0.5, 1);
    sprite.setDepth(REMOTE_DEPTH);
    if (this.active.anims.exists('painted-walker-idle')) {
      sprite.play('painted-walker-idle');
    }
    return sprite;
  }

  private applyDirectionalAnim(rec: RenderedRemote, dx: number, dy: number, moved: boolean): void {
    if (!this.active) return;
    if (!moved) {
      if (this.active.anims.exists('painted-walker-idle')
          && rec.sprite.anims.currentAnim?.key !== 'painted-walker-idle') {
        rec.sprite.play('painted-walker-idle');
      }
      return;
    }
    let anim: string;
    let flipX = false;
    if (Math.abs(dx) > Math.abs(dy) * 0.6) {
      anim = 'painted-walker-walk-left';
      flipX = dx > 0;
    } else if (dy < 0) {
      anim = 'painted-walker-walk-up';
    } else {
      anim = 'painted-walker-walk-down';
    }
    if (this.active.anims.exists(anim) && rec.sprite.anims.currentAnim?.key !== anim) {
      rec.sprite.play(anim);
    }
    rec.sprite.flipX = flipX;
  }
}
